import React from 'react';
import clsx from 'clsx';
import { Settings } from '@mui/icons-material';
import { Divider, Tooltip, Typography } from '@mui/material';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './ChatCalendarTabbedSideBar.scss';
import { ChatSideBar } from '../../AiAssistantChat';
import { CalendarsList } from '../CalendarsList';
import { TodoList } from '../TodoList';
import { GeneralCTA } from '../GeneralCTA';
import {
  settingsSidebarStateAtom,
  sidebarOpenAtom,
  syncErrorRootEmailAtom,
  useAssistantChat,
  useCalendarContext,
  isAllPlanPanelOpenAtom,
} from '../../../data-access';
import { useHandleCalSyncError } from '../../../hooks';
import { Path } from '../../../routing';
import { ReactComponent as NewChatIcon } from '../../../assets/chat/new-chat.svg';
import { SIDEBAR_MENU } from '../../../pages/DashboardPage/SideBar/constants';

type TabKey = 'chat' | 'settings';

interface ChatCalendarTabbedSideBarProps {
  className?: string;
  defaultTab?: TabKey;
  showGeneralCTA?: boolean;
  collapsible?: boolean;
  showCalendarTab?: boolean;
}

export const ChatCalendarTabbedSideBar: React.FC<ChatCalendarTabbedSideBarProps> = ({
  className,
  defaultTab = 'chat',
  showGeneralCTA = true,
  collapsible = false,
  showCalendarTab = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
  const syncErrorRootEmail = useRecoilValue(syncErrorRootEmailAtom);
  const isAllPlanPanelOpen = useRecoilValue(isAllPlanPanelOpenAtom);
  const { setOpenAssistantChat, clearMessages, messages, isSchedulerMode } = useAssistantChat();
  const { calendarsMeta, visibleCalendarIds, isFetchingCalendars, handleSelectCalendar } =
    useCalendarContext();

  const { handleConnect, handleDisconnect, isConnectingCalendars, isDisconnectingCalendars } =
    useHandleCalSyncError({ syncErrorRootEmail });

  const [activeTab, setActiveTab] = React.useState<TabKey>(showCalendarTab ? defaultTab : 'chat');
  const [open, setOpen] = useRecoilState(sidebarOpenAtom);

  React.useEffect(() => {
    localStorage.setItem('sidebarOpen', open ? 'true' : 'false');
  }, [open]);

  React.useEffect(() => {
    if (isAllPlanPanelOpen) {
      setActiveTab('chat');
      setOpenAssistantChat(true);
    }
  }, [isAllPlanPanelOpen, setOpenAssistantChat]);

  const handleNewChat = () => {
    clearMessages();
    setActiveTab('chat');
    setOpenAssistantChat(true);
  };

  const openAiAssistantSettings = () => {
    setSettingsSidebarState({
      selectedParentKey: 'aiAssistant',
      expandedParentKeys: [],
    });
    navigate(`${Path.HOME}/settings`);
    setOpenAssistantChat(false);
  };

  const newChatLabel = t('task.common.newChat').replace(/^\+\s*/, '');
  const isNewChat =
    !isSchedulerMode &&
    (messages.length === 0 ||
      (messages.length === 1 && messages[0]?.role === 'assistant' && !!messages[0]?.start));

  return (
    <div
      className={clsx(
        'chat-calendar-tabbed-side-bar',
        (activeTab === 'chat' || !showCalendarTab) && 'chat-calendar-tabbed-side-bar--chat-active',
        !showCalendarTab && 'chat-calendar-tabbed-side-bar--single-tab',
        className
      )}
    >
      <div className="chat-calendar-tabbed-side-bar__header">
        <div
          className="chat-calendar-tabbed-side-bar__tab-bar"
          role="tablist"
          aria-label="Sidebar tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'chat'}
            className={clsx(
              'chat-calendar-tabbed-side-bar__tab',
              activeTab === 'chat' && 'chat-calendar-tabbed-side-bar__tab--active'
            )}
            onClick={() => setActiveTab('chat')}
          >
            <span className="chat-calendar-tabbed-side-bar__tab-chat-content">
              <span className="chat-calendar-tabbed-side-bar__tab-chat-label">Chat</span>
              <span
                role="button"
                tabIndex={0}
                aria-label="Open AI Assistant settings"
                className="chat-calendar-tabbed-side-bar__tab-chat-label__settings-button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openAiAssistantSettings();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    openAiAssistantSettings();
                  }
                }}
              >
                <Settings className="chat-calendar-tabbed-side-bar__tab-chat-label__settings-button__icon" />
              </span>
            </span>
          </button>
          {showCalendarTab && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'settings'}
              className={clsx(
                'chat-calendar-tabbed-side-bar__tab',
                activeTab === 'settings' && 'chat-calendar-tabbed-side-bar__tab--active'
              )}
              onClick={() => setActiveTab('settings')}
            >
              Calendar
            </button>
          )}
        </div>
        <div className="chat-calendar-tabbed-side-bar__header__actions">
          {!isNewChat && (
            <Tooltip title={newChatLabel} placement="bottom">
              <button
                type="button"
                data-testid="chat-calendar-sidebar-new-chat-button"
                className="chat-calendar-tabbed-side-bar__header__new-chat"
                aria-label={newChatLabel}
                onClick={handleNewChat}
              >
                <NewChatIcon />
              </button>
            </Tooltip>
          )}
          {collapsible && (
            <button
              onClick={() => setOpen(false)}
              className="chat-calendar-tabbed-side-bar__header__menu-icon"
              aria-label={t('task.sidebar.collapseSidebar')}
              type="button"
            >
              <SIDEBAR_MENU.close title={t('task.sidebar.closeMenu')} />
            </button>
          )}
        </div>
      </div>

      <div className="chat-calendar-tabbed-side-bar__content">
        {activeTab === 'chat' || !showCalendarTab ? (
          <div className="chat-calendar-tabbed-side-bar__panel">
            <ChatSideBar />
          </div>
        ) : (
          <div className="chat-calendar-tabbed-side-bar__panel">
            <Typography variant="body1" className="chat-calendar-tabbed-side-bar__panel-subtitle">
              Calendars
            </Typography>
            <CalendarsList
              calendarsMeta={calendarsMeta}
              visibleCalendarIds={visibleCalendarIds}
              isFetchingCalendars={isFetchingCalendars}
              syncErrorRootEmail={syncErrorRootEmail}
              onSelectCalendar={handleSelectCalendar}
              onDisconnectCalendar={handleDisconnect}
              onConnectCalendar={handleConnect}
              isConnectingCalendars={isConnectingCalendars}
              isDisconnectingCalendars={isDisconnectingCalendars}
            />
            <Divider className="chat-calendar-tabbed-side-bar__panel-divider" />
            <Typography variant="body1" className="chat-calendar-tabbed-side-bar__panel-subtitle">
              TODOs
            </Typography>
            <TodoList />
          </div>
        )}
      </div>

      {showCalendarTab && showGeneralCTA && activeTab === 'settings' && (
        <GeneralCTA className="chat-calendar-tabbed-side-bar__general-cta" />
      )}
    </div>
  );
};
